import { supabase } from './supabase';
import { getUserBusinessId } from './supabaseDb';

export interface DbResult<T> {
  data: T | null;
  error: string | null;
}

function handleError(err: any): string {
  if (!err) return 'Bilinmeyen hata';
  return err.message || err.details || String(err);
}

function sanitizeItem<T extends Record<string, any>>(item: T): T {
  const cleaned: any = {};
  for (const [key, value] of Object.entries(item)) {
    if (value === undefined) continue;
    cleaned[key] = value;
  }
  return cleaned as T;
}

// ============================================================
// EMPLOYEES
// ============================================================

export async function getEmployees(): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('business_id', bizId)
      .order('full_name', { ascending: true });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getEmployeeById(id: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('id', id)
      .eq('business_id', bizId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createEmployee(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('employees')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function updateEmployee(id: string, data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: updated, error } = await supabase
      .from('employees')
      .update({ ...sanitizeItem(data), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: updated, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteEmployee(id: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from('employees')
      .delete()
      .eq('id', id)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function generateEmployeeNo(): Promise<DbResult<string>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const year = new Date().getFullYear().toString();
    const prefix = `PER-${year}-`;

    const { data, error } = await supabase
      .from('employees')
      .select('employee_no')
      .eq('business_id', bizId)
      .like('employee_no', `${prefix}%`)
      .order('employee_no', { ascending: false })
      .limit(1);

    if (error && error.code !== 'PGRST116') return { data: null, error: handleError(error) };

    let maxNum = 0;
    if (data && data.length > 0) {
      const parts = (data[0].employee_no || '').split('-');
      maxNum = parseInt(parts[2] || '0', 10);
    }

    return { data: `${prefix}${String(maxNum + 1).padStart(3, '0')}`, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// EMPLOYEE PAYMENTS
// ============================================================

export async function getEmployeePayments(employeeId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('employee_payments')
      .select('*')
      .eq('business_id', bizId)
      .eq('employee_id', employeeId)
      .order('payment_date', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getAllEmployeePayments(): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('employee_payments')
      .select('*, employees(full_name, department)')
      .eq('business_id', bizId)
      .order('payment_date', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createEmployeePayment(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('employee_payments')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteEmployeePayment(id: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from('employee_payments')
      .delete()
      .eq('id', id)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// EMPLOYEE LEAVES
// ============================================================

export async function getEmployeeLeaves(employeeId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('employee_leaves')
      .select('*')
      .eq('business_id', bizId)
      .eq('employee_id', employeeId)
      .order('start_date', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createEmployeeLeave(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('employee_leaves')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteEmployeeLeave(id: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from('employee_leaves')
      .delete()
      .eq('id', id)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// EMPLOYEE OVERTIMES
// ============================================================

export async function getEmployeeOvertimes(employeeId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('employee_overtimes')
      .select('*')
      .eq('business_id', bizId)
      .eq('employee_id', employeeId)
      .order('overtime_date', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createEmployeeOvertime(data: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: created, error } = await supabase
      .from('employee_overtimes')
      .insert({ ...sanitizeItem(data), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function deleteEmployeeOvertime(id: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from('employee_overtimes')
      .delete()
      .eq('id', id)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// COMPLEX: Record salary payment with expense + cash movement
// ============================================================

export async function recordEmployeeSalaryPayment(payload: {
  employee_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  description: string;
  created_by?: string;
}): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: employee } = await getEmployeeById(payload.employee_id);
    if (!employee) return { data: null, error: 'Personel bulunamadı' };

    // 1. Create employee payment record
    const { data: payment, error: payErr } = await createEmployeePayment({
      employee_id: payload.employee_id,
      payment_type: 'Maaş',
      amount: payload.amount,
      payment_date: payload.payment_date,
      payment_method: payload.payment_method,
      description: payload.description || `${(employee as any).full_name} maaş ödemesi`,
      created_by: payload.created_by || null,
    });

    if (payErr || !payment) return { data: null, error: payErr || 'Personel ödemesi kaydedilemedi' };

    // 2. Create expense record
    const { data: expense, error: expErr } = await supabase
      .from('expenses')
      .insert({
        business_id: bizId,
        category: 'Maaş',
        description: `${(employee as any).full_name} - ${payload.description || 'Maaş ödemesi'}`,
        amount: payload.amount,
        payment_method: payload.payment_method,
        date: payload.payment_date,
        employee_id: payload.employee_id,
        status: 'Ödendi',
      })
      .select()
      .single();

    if (expErr) {
      // Rollback payment
      await deleteEmployeePayment((payment as any).id);
      return { data: null, error: handleError(expErr) };
    }

    // 3. Create cash movement (expense)
    const { error: cashErr } = await supabase
      .from('cash_movements')
      .insert({
        business_id: bizId,
        type: 'Gider',
        description: `${(employee as any).full_name} - Maaş ödemesi`,
        amount: payload.amount,
        date: payload.payment_date,
        movement_date: payload.payment_date,
        category: 'Maaş',
        payment_method: payload.payment_method,
        expense_id: (expense as any).id,
        employee_id: payload.employee_id,
        recorded_by: (employee as any).full_name,
      });

    if (cashErr) {
      console.warn('Cash movement creation failed (non-critical):', cashErr);
    }

    return { data: { payment, expense }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// EMPLOYEE STATS for detail page
// ============================================================

export async function getEmployeeStats(employeeId: string): Promise<DbResult<{
  total_sales: number;
  total_sales_count: number;
  total_collections: number;
  total_collections_count: number;
  total_deliveries: number;
  monthly_sales: number;
  monthly_collections: number;
  monthly_bonus: number;
}>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const today = now.toISOString().split('T')[0];

    const [salesRes, monthlySalesRes, paymentsRes, monthlyPaymentsRes, deliveriesRes] = await Promise.all([
      supabase.from('sales').select('total_amount').eq('business_id', bizId).eq('employee_id', employeeId),
      supabase.from('sales').select('total_amount').eq('business_id', bizId).eq('employee_id', employeeId).gte('sale_date', monthStart).lte('sale_date', today),
      supabase.from('payments').select('amount').eq('business_id', bizId).eq('employee_id', employeeId),
      supabase.from('payments').select('amount').eq('business_id', bizId).eq('employee_id', employeeId).gte('payment_date', monthStart).lte('payment_date', today),
      supabase.from('deliveries').select('id', { count: 'exact' }).eq('business_id', bizId).eq('employee_id', employeeId),
    ]);

    const total_sales = (salesRes.data || []).reduce((s: number, r: any) => s + parseFloat(r.total_amount || '0'), 0);
    const total_sales_count = (salesRes.data || []).length;
    const monthly_sales = (monthlySalesRes.data || []).reduce((s: number, r: any) => s + parseFloat(r.total_amount || '0'), 0);
    const total_collections = (paymentsRes.data || []).reduce((s: number, r: any) => s + parseFloat(r.amount || '0'), 0);
    const total_collections_count = (paymentsRes.data || []).length;
    const monthly_collections = (monthlyPaymentsRes.data || []).reduce((s: number, r: any) => s + parseFloat(r.amount || '0'), 0);
    const total_deliveries = deliveriesRes.count || 0;

    // Simple bonus: 2% of monthly sales
    const monthly_bonus = Math.round(monthly_sales * 0.02 * 100) / 100;

    return {
      data: {
        total_sales,
        total_sales_count,
        total_collections,
        total_collections_count,
        total_deliveries,
        monthly_sales,
        monthly_collections,
        monthly_bonus,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// EMPLOYEE SALES / PAYMENTS / DELIVERIES lists
// ============================================================

export async function getEmployeeSales(employeeId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('sales')
      .select('*')
      .eq('business_id', bizId)
      .eq('employee_id', employeeId)
      .order('sale_date', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getEmployeePaymentsList(employeeId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('business_id', bizId)
      .eq('employee_id', employeeId)
      .order('payment_date', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getEmployeeDeliveries(employeeId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('deliveries')
      .select('*')
      .eq('business_id', bizId)
      .eq('employee_id', employeeId)
      .order('delivery_date', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getEmployeeActivityHistory(employeeId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const [paymentsRes, leavesRes, overtimesRes] = await Promise.all([
      supabase.from('employee_payments').select('*').eq('business_id', bizId).eq('employee_id', employeeId).order('created_at', { ascending: false }).limit(20),
      supabase.from('employee_leaves').select('*').eq('business_id', bizId).eq('employee_id', employeeId).order('created_at', { ascending: false }).limit(10),
      supabase.from('employee_overtimes').select('*').eq('business_id', bizId).eq('employee_id', employeeId).order('created_at', { ascending: false }).limit(10),
    ]);

    const activities: any[] = [];

    (paymentsRes.data || []).forEach((p: any) => {
      activities.push({
        id: p.id,
        type: 'payment',
        title: p.payment_type || 'Ödeme',
        description: `${p.payment_type}: ${parseFloat(p.amount || 0).toLocaleString('tr-TR')} ₺`,
        date: p.payment_date || p.created_at,
        icon: 'ri-money-dollar-circle-line',
        color: 'bg-emerald-100 text-emerald-700',
      });
    });

    (leavesRes.data || []).forEach((l: any) => {
      activities.push({
        id: l.id,
        type: 'leave',
        title: l.leave_type || 'İzin',
        description: `${l.start_date} - ${l.end_date} (${l.total_days} gün)`,
        date: l.start_date || l.created_at,
        icon: 'ri-calendar-check-line',
        color: 'bg-amber-100 text-amber-700',
      });
    });

    (overtimesRes.data || []).forEach((o: any) => {
      activities.push({
        id: o.id,
        type: 'overtime',
        title: 'Fazla Mesai',
        description: `${o.overtime_date}: ${o.hours} saat (${parseFloat(o.amount || 0).toLocaleString('tr-TR')} ₺)`,
        date: o.overtime_date || o.created_at,
        icon: 'ri-timer-line',
        color: 'bg-blue-100 text-blue-700',
      });
    });

    activities.sort((a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime());

    return { data: activities.slice(0, 30), error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}